import React, { useState, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "../lib/api";
import { UploadCloud, FileSpreadsheet, Loader2, ArrowRight, BookOpen, Check } from "lucide-react";

export default function Upload() {
  const [file, setFile] = useState(null);
  const [name, setName] = useState("");
  const [periodDays, setPeriodDays] = useState("");
  const [notes, setNotes] = useState("");
  const [dragActive, setDragActive] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [inspection, setInspection] = useState(null);
  // null = before inspect, "respect" or "botplus" once user chooses
  const [classification, setClassification] = useState(null);
  const inputRef = useRef(null);
  const navigate = useNavigate();

  const onDrop = (e) => {
    e.preventDefault();
    setDragActive(false);
    const f = e.dataTransfer.files?.[0];
    if (f) { setFile(f); setInspection(null); setClassification(null); }
  };

  const onPick = (f) => {
    setFile(f);
    setInspection(null);
    setClassification(null);
  };

  // Step 1: lightweight inspection to decide whether to ask about classification
  const submitInspect = async (e) => {
    e.preventDefault();
    if (!file || !name) {
      setError("Indica el nombre del estudio y selecciona un archivo Excel.");
      return;
    }
    setLoading(true);
    setError("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/studies/inspect-headers", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setInspection(data);
      const coveragePct = data.familia_coverage_pct || 0;
      // If the Excel has no familia column or fewer than 5% rows filled → no question, go straight to BOT PLUS
      if (!data.has_familia_column || coveragePct < 0.05) {
        await doSubmit("botplus");
      }
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  // Step 2: actual study creation with classification choice
  const doSubmit = async (choice) => {
    setClassification(choice);
    setLoading(true);
    setError("");
    const fd = new FormData();
    fd.append("file", file);
    fd.append("name", name);
    if (periodDays) fd.append("period_days", periodDays);
    if (notes) fd.append("notes", notes);
    fd.append("reclassify_with_botplus", choice === "botplus" ? "true" : "false");
    try {
      const { data } = await api.post("/studies/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      navigate(`/studies/${data.id}`);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      setClassification(null);
    } finally {
      setLoading(false);
    }
  };

  const showQuestion = inspection && inspection.has_familia_column && (inspection.familia_coverage_pct >= 0.05) && !classification;

  return (
    <div className="p-8 fade-in max-w-4xl" data-testid="upload-page">
      <div className="text-xs font-semibold uppercase tracking-widest text-slate-500">Nuevo estudio</div>
      <h1 className="font-display text-4xl font-extrabold tracking-tight mt-1 mb-2">Subir listado Excel</h1>
      <p className="text-slate-600 mb-4 max-w-2xl">
        El archivo debe contener al menos las columnas: <span className="font-semibold">CN, Stock, PVP, Uds. Vendidas, Laboratorio</span>.
        La columna <span className="font-semibold">Familia</span> es opcional: si no la tienes, clasificaremos automáticamente con el catálogo oficial BOT PLUS.
      </p>

      {/* Farmatic shortcut */}
      <div className="mb-6 p-4 rounded-lg border-2 border-blue-200 bg-blue-50 flex items-start gap-3" data-testid="farmatic-shortcut">
        <div className="h-9 w-9 rounded-lg bg-blue-600 text-white flex items-center justify-center shrink-0">F</div>
        <div className="flex-1">
          <div className="font-display font-bold text-blue-900">¿Usas Farmatic?</div>
          <p className="text-xs text-blue-900 mt-0.5">
            Si exportas directamente desde Farmatic puedes subir los <b>3 ficheros</b> (Inventario · Venta Libre · Venta Receta) sin tener que combinarlos.
          </p>
        </div>
        <Link to="/upload/farmatic" className="btn btn-primary btn-sm" data-testid="farmatic-link">
          Importar desde Farmatic
        </Link>
      </div>

      <form onSubmit={submitInspect} className="space-y-5">
        <div
          className={"dropzone " + (dragActive ? "active" : "")}
          onDragOver={(e) => { e.preventDefault(); setDragActive(true); }}
          onDragLeave={() => setDragActive(false)}
          onDrop={onDrop}
          onClick={() => inputRef.current?.click()}
          data-testid="dropzone"
          style={{ cursor: "pointer" }}
        >
          <input
            ref={inputRef}
            type="file"
            accept=".xlsx,.xls,.xlsm"
            className="hidden"
            onChange={(e) => onPick(e.target.files?.[0] || null)}
            data-testid="file-input"
          />
          {file ? (
            <div className="flex flex-col items-center gap-2">
              <FileSpreadsheet size={36} className="text-emerald-500" />
              <div className="font-semibold">{file.name}</div>
              <div className="text-xs text-slate-500">{(file.size / 1024).toFixed(0)} KB</div>
            </div>
          ) : (
            <div className="flex flex-col items-center gap-2">
              <UploadCloud size={36} className="text-slate-400" />
              <div className="font-semibold">Arrastra tu Excel aquí o haz clic para seleccionar</div>
              <div className="text-xs text-slate-500">.xlsx, .xls, .xlsm</div>
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="text-xs font-semibold uppercase tracking-widest text-slate-500">Nombre del estudio *</label>
            <input data-testid="study-name-input" required value={name} onChange={(e) => setName(e.target.value)}
                   className="input mt-1" placeholder="Ej: Estudio anual 2024" />
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-widest text-slate-500">Periodo (días) — opcional</label>
            <input data-testid="period-days-input" type="number" min="1" value={periodDays} onChange={(e) => setPeriodDays(e.target.value)}
                   className="input mt-1" placeholder="Auto-detectar (365 si no hay fechas)" />
            <p className="text-xs text-slate-500 mt-1">Si lo dejas vacío, se detectará automáticamente del rango de fechas. Los umbrales ABCD se escalarán a este periodo.</p>
          </div>
        </div>

        <div>
          <label className="text-xs font-semibold uppercase tracking-widest text-slate-500">Notas (opcional)</label>
          <textarea data-testid="notes-input" rows={2} value={notes} onChange={(e) => setNotes(e.target.value)}
                    className="input mt-1" placeholder="Comentarios sobre este estudio" />
        </div>

        {error && (
          <div data-testid="upload-error" className="text-sm text-red-700 bg-red-50 px-3 py-2 rounded border border-red-200">{error}</div>
        )}

        {!inspection && (
          <button data-testid="upload-submit-button" type="submit" disabled={loading} className="btn btn-primary">
            {loading ? <><Loader2 size={16} className="animate-spin" /> Analizando archivo…</> : <>Continuar <ArrowRight size={16} /></>}
          </button>
        )}
      </form>

      {/* Classification question */}
      {showQuestion && (
        <div className="mt-8 border-2 border-blue-300 bg-blue-50 rounded-lg p-6 fade-in" data-testid="classification-dialog">
          <div className="flex items-start gap-3">
            <div className="h-10 w-10 rounded-lg bg-blue-600 text-white flex items-center justify-center shrink-0">
              <BookOpen size={20} />
            </div>
            <div className="flex-1">
              <h3 className="font-display text-xl font-bold text-blue-900">Tu farmacia ya tiene una clasificación por familias</h3>
              <p className="text-sm text-blue-800 mt-1">
                He detectado la columna <b>Familia</b> con <b>{inspection.familia_filled.toLocaleString("es-ES")}</b> de {inspection.rows.toLocaleString("es-ES")} productos rellenos
                ({(inspection.familia_coverage_pct * 100).toFixed(0)}% de cobertura).
              </p>
              <p className="text-sm text-blue-800 mt-3"><b>¿Quieres respetar tu clasificación, o reclasificar la parafarmacia con el catálogo oficial BOT PLUS ({inspection.botplus_catalog_size.toLocaleString("es-ES")} productos)?</b></p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-5">
                <button
                  onClick={() => doSubmit("respect")}
                  disabled={loading}
                  data-testid="classify-respect-button"
                  className="text-left p-4 rounded-lg border-2 border-slate-300 bg-white hover:border-slate-900 hover:shadow-md transition disabled:opacity-50"
                >
                  <div className="flex items-center gap-2 font-bold text-slate-900">
                    <Check size={16} /> Respetar mi clasificación
                  </div>
                  <p className="text-xs text-slate-600 mt-1">Usa la columna Familia tal cual la tengo en mi Excel.</p>
                </button>
                <button
                  onClick={() => doSubmit("botplus")}
                  disabled={loading || inspection.botplus_catalog_size === 0}
                  data-testid="classify-botplus-button"
                  className="text-left p-4 rounded-lg border-2 border-blue-600 bg-blue-600 text-white hover:bg-blue-700 hover:shadow-md transition disabled:opacity-50"
                >
                  <div className="flex items-center gap-2 font-bold">
                    <BookOpen size={16} /> Reclasificar con BOT PLUS
                  </div>
                  <p className="text-xs text-blue-100 mt-1">Sobrescribe la familia de los productos de parafarmacia (CN &lt; 400.000) con la categoría oficial. Farmacia se mantiene.</p>
                  {inspection.botplus_catalog_size === 0 && (
                    <p className="text-[10px] text-yellow-200 mt-1">⚠️ Catálogo BOT PLUS no cargado. Pide al admin que lo suba.</p>
                  )}
                </button>
              </div>
              {loading && <div className="mt-4 text-sm text-blue-900 flex items-center gap-2"><Loader2 className="animate-spin" size={14}/> Procesando estudio…</div>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
